import { useContext } from 'react';
import { Form, Formik } from 'formik';

import * as api from '../../../../../../api';

import {
  ContinueButton,
  ErrorHint,
  InputLabel,
  ReconciliationContainer,
  Row,
} from './common';
import { ConfigurationContext } from '../../../../../../context/configurationContext';
import MessageContent, { getMessageContent } from '../../messageContent';
import type { ChatConversation } from '../../../../../../common';

interface Props {
  message: ChatConversation;
  provisionalPatientId: string;
  showTextAnimation: boolean;
  onReconciliationSuccess: () => void;
  showMessageAnswer?: boolean;
}

const VerificationCode: React.FC<Props> = ({
  message,
  provisionalPatientId,
  showTextAnimation,
  onReconciliationSuccess,
  showMessageAnswer,
}) => {
  const configurationContext = useContext(ConfigurationContext);

  const handleSubmitCode = async (
    values: { code: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    if (!configurationContext?.id) return;
    if (!configurationContext.setAuthenticated) return;
    if (!configurationContext.setReconciliationStatus) return;
    if (!configurationContext.sessionId) return;
    try {
      const { success, reconciliationStatus } =
        await api.client.Previsit.verifyAuthPatientForAssistant({
          configurationId: configurationContext?.id,
          provisionalPatientId,
          code: values.code,
          sessionId: configurationContext.sessionId,
        });
      onReconciliationSuccess();
      configurationContext.setAuthenticated(success);
      configurationContext.setReconciliationStatus(reconciliationStatus);
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  };

  return (
    <>
      <MessageContent
        content={
          (showMessageAnswer ? `${getMessageContent(message)}\n` : '') +
          `I just sent you a text and an email with a 6-digit code. Add the code below to continue.`
        }
        showTextAnimation={true}
        isAgent={true}
        isLoading={false}
      />
      <ReconciliationContainer
        $delaySecond={0.3}
        data-test-id="authentication-verification-code"
      >
        <Formik
          initialValues={{
            code: '',
          }}
          onSubmit={handleSubmitCode}
          validate={(values) => {
            const codeError =
              values.code && !/^\d{6}$/.test(values.code)
                ? 'Invalid code'
                : undefined;
            if (!codeError) return;
            return { code: codeError };
          }}
        >
          {({ isSubmitting, values, handleChange }) => (
            <Form>
              <Row className="row">
                <div className="col">
                  <InputLabel htmlFor="code">6-digit code</InputLabel>
                  <input
                    data-test-id="authentication-verification-code-input"
                    type="text"
                    id="code"
                    name="code"
                    pattern="\d*"
                    className="form-control"
                    value={values.code}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting || message.disabled}
                    autoFocus
                  />
                  <ErrorHint name="code" component="div" />
                </div>
              </Row>
              <Row className="row">
                <div className="col">
                  <ContinueButton
                    disabled={isSubmitting || message.disabled}
                    type="submit"
                  >
                    Continue
                  </ContinueButton>
                </div>
              </Row>
            </Form>
          )}
        </Formik>
      </ReconciliationContainer>
    </>
  );
};

export default VerificationCode;
